import * as THREE from 'three';
import Application from '../Application';
import { BRUNO_DESK } from './DecorMode';
/**
 * A procedural water-cooled PC tower with animated RGB fans, built entirely
 * from Three.js primitives (no external asset needed). Purely additive — it
 * doesn't touch the baked room, the monitor iframe or the camera.
 *
 * The scene is unlit (baked MeshBasicMaterial everywhere), so "glow" here means
 * bright, self-coloured MeshBasicMaterial — point lights wouldn't affect the
 * baked room anyway.
 *
 * Placement is derived from the desk's bounding box (stood on the floor beside
 * it). Fine-tune live via URL params without rebuilding, e.g.:
 *   ?towerScale=1.1&towerX=200&towerZ=-100&towerSide=left
 */
export default class WatercoolingTower {
    constructor() {
        this.fanRings = [];
        this.stripMaterials = [];
        this.application = new Application();
        this.scene = this.application.scene;
        this.time = this.application.time;
        this.group = new THREE.Group();
        // Le poste importe du dossier 1 a deja sa tour, sous le plateau. On
        // garde l'objet (World.update l'appelle) mais on ne construit rien :
        // ses tableaux de ventilateurs restent vides, donc update ne fait
        // rien non plus.
        if (!BRUNO_DESK)
            this.build();
        this.scene.add(this.group);
    }
    params() {
        const p = new URLSearchParams(window.location.search);
        const num = (k, d) => {
            const v = p.get(k);
            return v === null ? d : parseFloat(v);
        };
        return {
            scale: num('towerScale', 1),
            offsetX: num('towerX', 0),
            offsetY: num('towerY', 0),
            offsetZ: num('towerZ', 0),
            side: (p.get('towerSide') || 'right'),
        };
    }
    /** Work out where the desk is so we can stand the tower next to it. */
    placement() {
        const { scale, offsetX, offsetY, offsetZ, side } = this.params();
        const desk = this.scene.getObjectByName('desk');
        // Sensible fallback if the desk node can't be found for some reason.
        let box = new THREE.Box3(new THREE.Vector3(-1400, 0, -600), new THREE.Vector3(1400, 1400, 600));
        if (desk) {
            desk.updateWorldMatrix(true, true);
            box = new THREE.Box3().setFromObject(desk);
        }
        const deskHeight = box.max.y - box.min.y;
        const height = deskHeight * 0.82 * scale;
        const width = height * 0.42;
        const depth = height * 0.95;
        const gap = width * 0.6;
        const x = (side === 'left' ? box.min.x - gap - width / 2 : box.max.x + gap + width / 2) +
            offsetX;
        const z = (box.min.z + depth / 2) + offsetZ;
        const y = box.min.y + height / 2 + offsetY;
        return { width, height, depth, position: new THREE.Vector3(x, y, z) };
    }
    build() {
        const { width, height, depth, position } = this.placement();
        this.group.position.copy(position);
        const half = { w: width / 2, h: height / 2, d: depth / 2 };
        // --- Case body (dark matte) ---
        const caseMat = new THREE.MeshBasicMaterial({ color: 0x14151a });
        const body = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), caseMat);
        this.group.add(body);
        // Subtle edge frame so the box reads as a case, not a flat block.
        const edges = new THREE.LineSegments(new THREE.EdgesGeometry(body.geometry), new THREE.LineBasicMaterial({ color: 0x2a2c34 }));
        this.group.add(edges);
        // --- Tempered-glass side panel (left face, facing the room) ---
        const glass = new THREE.Mesh(new THREE.PlaneGeometry(depth * 0.86, height * 0.86), new THREE.MeshBasicMaterial({
            color: 0x0a1420,
            transparent: true,
            opacity: 0.35,
            side: THREE.DoubleSide,
            depthWrite: false,
        }));
        glass.rotation.y = Math.PI / 2;
        glass.position.x = -half.w - 1;
        this.group.add(glass);
        // --- Front RGB fans ---
        const fanCount = 3;
        const fanRadius = width * 0.34;
        const spacing = (height * 0.86) / fanCount;
        const startY = -spacing * (fanCount - 1) * 0.5;
        const frontZ = half.d + 1;
        for (let i = 0; i < fanCount; i++) {
            const fan = this.buildFan(fanRadius);
            fan.position.set(0, startY + i * spacing, frontZ);
            this.group.add(fan);
        }
        // --- Vertical RGB light strip behind the glass ---
        const stripMat = new THREE.MeshBasicMaterial({ color: 0xff00ff });
        this.stripMaterials.push(stripMat);
        const strip = new THREE.Mesh(new THREE.BoxGeometry(width * 0.04, height * 0.8, depth * 0.04), stripMat);
        strip.position.set(-half.w * 0.7, 0, -half.d * 0.6);
        this.group.add(strip);
    }
    /** One RGB fan: bright coloured ring + dark hub and blades. */
    buildFan(radius) {
        const fan = new THREE.Group();
        const ringMat = new THREE.MeshBasicMaterial({ color: 0xff0000 });
        this.fanRings.push(
        // remember the ring mesh's material for the RGB animation
        new THREE.Mesh(new THREE.TorusGeometry(radius, radius * 0.12, 12, 40), ringMat));
        const ring = this.fanRings[this.fanRings.length - 1];
        fan.add(ring);
        // Dark backing so the ring reads as a fan, not a floating loop.
        const backing = new THREE.Mesh(new THREE.CircleGeometry(radius * 0.92, 32), new THREE.MeshBasicMaterial({ color: 0x0b0c10 }));
        backing.position.z = -radius * 0.1;
        fan.add(backing);
        // Hub + a few blades (dark).
        const hub = new THREE.Mesh(new THREE.CircleGeometry(radius * 0.28, 20), new THREE.MeshBasicMaterial({ color: 0x1b1d24 }));
        hub.position.z = 0.5;
        fan.add(hub);
        const bladeMat = new THREE.MeshBasicMaterial({
            color: 0x15161c,
            side: THREE.DoubleSide,
        });
        for (let b = 0; b < 7; b++) {
            const blade = new THREE.Mesh(new THREE.PlaneGeometry(radius * 0.62, radius * 0.28), bladeMat);
            blade.position.z = 0.2;
            blade.rotation.z = (b / 7) * Math.PI * 2;
            blade.position.x = Math.cos(blade.rotation.z) * radius * 0.42;
            blade.position.y = Math.sin(blade.rotation.z) * radius * 0.42;
            fan.add(blade);
        }
        return fan;
    }
    update() {
        const t = this.time.elapsed * 0.0004;
        // Each fan cycles through the spectrum, offset from its neighbours.
        this.fanRings.forEach((ring, i) => {
            const hue = (t + i * 0.18) % 1;
            ring.material.color.setHSL(hue, 1, 0.55);
        });
        this.stripMaterials.forEach((mat) => {
            const hue = (t * 0.7 + 0.5) % 1;
            mat.color.setHSL(hue, 1, 0.55);
        });
    }
}
